import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Link, Route, Routes, useLocation } from "react-router-dom";
import { Container, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";
import ErrorBoundary from './components/ErrorBoundary';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ConnectionStatus from './components/ConnectionStatus';

import Login from "./components/Login";
import Register from "./components/Register";
import WorkstationManager from "./components/WorkstationManager";
import WorkstationManagerView from "./components/WorkstationManagerView";
import MobileDeviceManager from "./components/MobileDeviceManager";
import Home from "./components/Home";
import Profile from "./components/Profile";
import BoardUser from "./components/BoardUser";
import BoardModerator from "./components/BoardModerator";
import BoardAdmin from "./components/BoardAdmin";

import { logout } from "./slices/auth";

import EventBus from "./common/EventBus";
import ChangePassword from "./components/ChangePassword";
import ForgetPassword from "./components/ForgetPassword";
import ResetPassword from "./components/ResetPassword";
import RegisterClient from "./components/RegisterClient";
import ClientSubscriptions from "./components/ClientSubscriptions";
import SubscriptionsMoneris from "./components/moneris/SubscriptionsMoneris";
import Checkout from "./components/Checkout";
import PaymentDeclined from "./components/moneris/PaymentDeclined";
import PaymentApproved from "./components/moneris/PaymentApproved";
import Receipt from "./components/moneris/Receipt";
import PaymentValidation from "./components/moneris/PaymentValidation";
import CreateUser from "./components/CreateUser";
import ClientDiscount from "./components/ClientDiscount";
import ResetUserPassword from "./components/ResetUserPassword";
import Inventory from "./components/Inventory";
import SubscriptionsStripe from "./components/stripe/SubscriptionsStripe";
import SubscriptionsResult from "./components/SubscriptionsResult";
import DesktopLogin from "./components/DesktopLogin";
import InventoryServiceLogin from "./components/InventoryServiceLogin";
import PartsPhoto from "./components/PartsPhoto";
import GlobalPromo from "./components/GlobalPromo";
import UploadMessagePage from "./components/UploadMessagePage";
import SoldHistory from "./components/inventory/report/SoldHistory";
import InventoryValue from "./components/inventory/report/InventoryValue";
import MediaClientManager from "./components/MediaClientManager";
import PhotobaseDevices from "./components/PhotobaseDevices";
import DataUsage from "./components/DataUsage";
import ROFolderDemo from "./components/ROFolderDemo";
import ROFolderDemoImg from "./components/ROFolderDemoImg";
import WorkstationIpAnalysis from "./components/WorkstationIpAnalysis";
import WorkstationIpConflictView from "./components/WorkstationIpConflictView";
import SyncBandwidth from "./components/SyncBandwidth";
import StorageSize from "./components/StorageSize";
import InstallKeyManager from "./components/InstallKeyManager";
import PKClientManager from "./components/PKClientManager";
import PKSubscriptionManager from "./components/PKSubscriptionManager";
import ClientShopWorkstationReport from "./components/ClientShopWorkstationReport";
import ShopWhiteListManager from "./components/ShopWhiteListManager";
import ShopBlackListManager from "./components/ShopBlackListManager";
import BlockedWorkstationManager from "./components/BlockedWorkstationManager";
import TrespassRequestManager from "./components/TrespassRequestManager";
import PKGroupManager from "./components/PKGroupManager";
import PKUsageSummary from "./components/PKUsageSummary";
import PartsAiResult from "./components/inventory/PartsAiResult";
import WorkstationVersion from "./components/WorkstationVersion";
import GptConfigManager from "./components/GptConfigManager";
import GlobalErrorModal from './components/GlobalErrorModal';
import { recordRecentArea } from "./common/recentAreas";

toast.error = (message, options) => {
    EventBus.dispatch("showError", message);
};

const RouteActivityTracker = () => {
  const location = useLocation();
  const { user: currentUser } = useSelector((state) => state.auth);

  useEffect(() => {
    recordRecentArea(currentUser, location.pathname);
  }, [currentUser, location.pathname]);

  return null;
};

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

const App = (props) => {
  const [showModeratorBoard, setShowModeratorBoard] = useState(false);
  const [showAdminBoard, setShowAdminBoard] = useState(false);
  const [showAiAdminBoard, setShowAiAdminBoard] = useState(false);
  const [navExpanded, setNavExpanded] = useState(false);
  const { user: currentUser } = useSelector((state) => state.auth);
  const isService = currentUser && currentUser.serviceType;
  const dispatch = useDispatch();

  const logOut = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser) {
      if (currentUser.accessToken) {
        const decodedJwt = parseJwt(currentUser.accessToken);
        if (decodedJwt && decodedJwt.exp * 1000 < Date.now()) {
          logOut();
          window.location.href = '/login?code=401';
          return;
        }
      }

      if (currentUser.roles) {
        setShowModeratorBoard(currentUser.roles.includes("ROLE_MODERATOR"));
        setShowAdminBoard(currentUser.roles.includes("ROLE_ADMIN"));
        setShowAiAdminBoard(currentUser.roles.includes("ROLE_AI_ADMIN"));
      } else {
        setShowModeratorBoard(false);
        setShowAdminBoard(false);
        setShowAiAdminBoard(false);
      }
    } else {
      setShowModeratorBoard(false);
      setShowAdminBoard(false);
      setShowAiAdminBoard(false);
    }

    EventBus.on("logout", () => {
      logOut();
    });

    return () => {
      EventBus.remove("logout");
    };
  }, [currentUser, logOut]);

  const [toggleClicked, setToggleClicked] = useState(false);

  const handleToggle = () => {
    setToggleClicked(!toggleClicked);
    setNavExpanded(!navExpanded);
  };

  const closeMenu = () => {
    setToggleClicked(false);
    setNavExpanded(false);
  };

  return (
    <ErrorBoundary>
      <Router>
        <div>
          <RouteActivityTracker />
          <ConnectionStatus />
          <ToastContainer />
          <GlobalErrorModal />
            <Navbar collapseOnSelect expand="lg" className="navbar-premium sticky-top shadow-sm py-3" expanded={navExpanded} onToggle={handleToggle}>
              <Container fluid className="px-4">
                <Navbar.Brand as={Link} to="/" onClick={() => closeMenu()} className="fw-bold d-flex align-items-center" style={{ fontSize: '1.4rem' }}>
                  <i className="bi bi-box-fill text-primary me-2"></i>
                  <span style={{ background: 'var(--primary-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>PartsKick</span>
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="responsive-navbar-nav" className="border-0 shadow-none" />
                <Navbar.Collapse id="responsive-navbar-nav" className="mt-3 mt-lg-0">
                  {!isService && (
                    <Nav className="me-auto fw-medium">
                      <NavDropdown title={<><i className="bi bi-grid-1x2 me-1"></i> Management</>} id="management-nav-dropdown">
                        {currentUser && currentUser.owner && (
                          <>
                            <NavDropdown.Item as={Link} to="/registerNewClient" onClick={() => closeMenu()}>Register New Client</NavDropdown.Item>
                            <NavDropdown.Divider />
                          </>
                        )}
                        <NavDropdown.Item as={Link} to="/workstation-manager" onClick={() => closeMenu()}>Workstation Manager</NavDropdown.Item>
                        <NavDropdown.Item as={Link} to="/mobile-device-manager" onClick={() => closeMenu()}>Mobile Device Manager</NavDropdown.Item>
                      </NavDropdown>
                      {showAdminBoard && (
                        <NavDropdown title={<><i className="bi bi-shield-lock me-1"></i> Admin</>} id="admin-nav-dropdown">
                          {showAdminBoard && (
                            <>
                              <NavDropdown.Header>PartsKick</NavDropdown.Header>
                              <NavDropdown.Item as={Link} to="/createUser" onClick={() => closeMenu()}>Create *Owner*</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/resetUserPassword" onClick={() => closeMenu()}>Reset User Password</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/clientDiscount" onClick={() => closeMenu()}>Set Client Discount</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/uploadMessagePage" onClick={() => closeMenu()}>Update Message Page</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/admin/install-keys" onClick={() => closeMenu()}>Install Key</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/admin/pk-clients" onClick={() => closeMenu()}>PK Client</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/admin/pk-subscriptions" onClick={() => closeMenu()}>PK Subscription</NavDropdown.Item>
                              <NavDropdown.Item as={Link} to="/admin/pk-groups" onClick={() => closeMenu()}>PK Group</NavDropdown.Item>
                              <NavDropdown.Divider />
                            </>
                          )}
                          {showAdminBoard && (
                            <>
                              <NavDropdown.Header>PhotoBase</NavDropdown.Header>
                              <NavDropdown.Item as={Link} to="/media-client" onClick={() => closeMenu()}>Media Client</NavDropdown.Item>
                            </>
                          )}
                        </NavDropdown>
                      )}
                      {showAiAdminBoard && (
                        <NavDropdown title={<><i className="bi bi-robot me-1"></i> AI</>} id="ai-nav-dropdown">
                          <NavDropdown.Item as={Link} to="/admin/gpt-configure" onClick={() => closeMenu()}>GPT Configure</NavDropdown.Item>
                        </NavDropdown>
                      )}
                      {showAdminBoard && (
                        <NavDropdown title={<><i className="bi bi-credit-card me-1"></i> Subscriptions</>} id="subscriptions-nav-dropdown">
                          <NavDropdown.Item as={Link} to="/subscriptions-moneris" onClick={() => closeMenu()}>Moneris</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/subscriptions-stripe" onClick={() => closeMenu()}>Stripe</NavDropdown.Item>
                        </NavDropdown>
                      )}

                      {showAdminBoard && (
                        <NavDropdown title={<><i className="bi bi-graph-up me-1"></i> Report</>} id="report-nav-dropdown">
                          <NavDropdown.Header>Workstation Processing Shop</NavDropdown.Header>
                          <NavDropdown.Item as={Link} to="/reports/client-shop-name" onClick={() => closeMenu()}>Workstation Shop Name</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/shop-white-list" onClick={() => closeMenu()}>Shop White List</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/shop-black-list" onClick={() => closeMenu()}>Shop Black List</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/blocked-workstations" onClick={() => closeMenu()}>Blocked Workstation</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/trespass-requests" onClick={() => closeMenu()}>Trespass Request</NavDropdown.Item>

                          <NavDropdown.Divider />
                          <NavDropdown.Header>Workstation</NavDropdown.Header>
                          <NavDropdown.Item as={Link} to="/reports/workstation-version" onClick={() => closeMenu()}>Version</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/workstation-ip-analysis" onClick={() => closeMenu()}>Workstation IP Analysis</NavDropdown.Item>
                          <NavDropdown.Divider />
                          <NavDropdown.Header>Cloud Usage Analysis</NavDropdown.Header>
                          <NavDropdown.Item as={Link} to="/reports/sync-bandwidth" onClick={() => closeMenu()}>Sync Bandwidth</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/storage-size" onClick={() => closeMenu()}>Storage Size</NavDropdown.Item>
                          <NavDropdown.Divider />
                          <NavDropdown.Header>PK Usage Summary</NavDropdown.Header>
                          <NavDropdown.Item as={Link} to="/reports/usage-summary-monthly" onClick={() => closeMenu()}>Monthly</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/usage-summary-weekly" onClick={() => closeMenu()}>Weekly</NavDropdown.Item>
                          <NavDropdown.Divider />
                          <NavDropdown.Header>Photobase Usage</NavDropdown.Header>
                          <NavDropdown.Item as={Link} to="/reports/photobase-usage/devices" onClick={() => closeMenu()}>Devices</NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/reports/data-usage" onClick={() => closeMenu()}>Data</NavDropdown.Item>
                        </NavDropdown>
                      )}

                      <NavDropdown title={<><i className="bi bi-box-seam me-1"></i> Inventory</>} id="inventory-nav-dropdown">
                        <NavDropdown.Item as={Link} to="/inventory" onClick={() => closeMenu()}>Inventory List</NavDropdown.Item>
                        <NavDropdown.Item as={Link} to="/inventoryValue" onClick={() => closeMenu()}>Inventory Value</NavDropdown.Item>
                        <NavDropdown.Item as={Link} to="/soldHistory" onClick={() => closeMenu()}>Sold History</NavDropdown.Item>
                        {showAdminBoard && (
                          <>
                            <NavDropdown.Divider />
                            <NavDropdown.Item as={Link} to="/inventory/parts-ai-result" onClick={() => closeMenu()}>Parts Number AI Result</NavDropdown.Item>
                          </>
                        )}
                      </NavDropdown>

                      {showModeratorBoard && (
                        <Nav.Link as={Link} to="/mod" onClick={() => closeMenu()}>Moderator Board</Nav.Link>
                      )}
                    </Nav>
                  )}
                  {currentUser ? (
                    <Nav className="fw-medium align-items-lg-center">
                      {!isService && (
                        <Nav.Link as={Link} to="/profile" onClick={() => closeMenu()} className="d-flex align-items-center">
                          <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '32px', height: '32px', fontSize: '0.8rem' }}>
                            {currentUser.username.substring(0, 2).toUpperCase()}
                          </div>
                          {currentUser.username}
                        </Nav.Link>
                      )}
                      {isService && <span className="me-3 badge bg-info" style={{alignSelf: 'center'}}>Service Access</span>}
                      <button className="btn btn-outline-danger btn-sm ms-lg-3 rounded-pill px-3 mt-2 mt-lg-0" onClick={logOut}>
                        <i className="bi bi-box-arrow-right me-1"></i> LogOut
                      </button>
                    </Nav>
                  ) : (
                    <Nav className="fw-medium">
                      <Nav.Link as={Link} to="/login" onClick={() => closeMenu()} className="me-2">Login</Nav.Link>
                      <Link to="/register" onClick={() => closeMenu()} className="btn btn-primary rounded-pill px-4 mt-2 mt-lg-0">Sign Up</Link>
                    </Nav>
                  )}
                </Navbar.Collapse>
              </Container>
            </Navbar>

            <div className="container mt-3">
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/home" element={<Home />} />
                <Route path="/workstation-manager" element={<WorkstationManager />} />
                <Route path="/workstation-manager-view" element={<WorkstationManagerView />} />
                <Route path="/mobile-device-manager" element={<MobileDeviceManager />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/user" element={<BoardUser />} />
                <Route path="/mod" element={<BoardModerator />} />
                <Route path="/admin" element={<BoardAdmin />} />
                <Route path="/changePassword" element={<ChangePassword />} />
                <Route path="/forgetPassword" element={<ForgetPassword />} />
                <Route path="/resetPassword" element={<ResetPassword />} />
                <Route path="/registerNewClient" element={<RegisterClient />} />
                <Route path="/manageSubscription" element={<ClientSubscriptions />} />
                <Route path="/subscriptions-moneris" element={<SubscriptionsMoneris />} />
                <Route path="/subscriptions-stripe" element={<SubscriptionsStripe />} />
                <Route path="/subscriptionsResult" element={<SubscriptionsResult />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/payment/declined" element={<PaymentDeclined />} />
                <Route path="/payment/approved" element={<PaymentApproved />} />
                <Route path="/payment/validation" element={<PaymentValidation />} />
                <Route path="/payment/receipt" element={<Receipt />} />
                <Route path="/createUser" element={<CreateUser />} />
                <Route path="/clientDiscount" element={<ClientDiscount />} />
                <Route path="/resetUserPassword" element={<ResetUserPassword />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/parts-photo" element={<PartsPhoto />} />
                <Route path="/desktop/login" element={<DesktopLogin />} />
                <Route path="/inventory-service/login" element={<InventoryServiceLogin />} />
                <Route path="/globalPromo" element={<GlobalPromo />} />
                <Route path="/uploadMessagePage" element={<UploadMessagePage />} />
                <Route path="/soldHistory" element={<SoldHistory />} />
                <Route path="/inventoryValue" element={<InventoryValue />} />
                <Route path="/admin/install-keys" element={<InstallKeyManager />} />
                <Route path="/admin/pk-clients" element={<PKClientManager />} />
                <Route path="/admin/pk-subscriptions" element={<PKSubscriptionManager />} />
                <Route path="/media-client" element={<MediaClientManager />} />
                <Route path="/reports/photobase-usage/devices" element={<PhotobaseDevices />} />
                <Route path="/reports/client-shop-name" element={<ClientShopWorkstationReport />} />
                <Route path="/reports/shop-white-list" element={<ShopWhiteListManager />} />
                <Route path="/reports/shop-black-list" element={<ShopBlackListManager />} />
                <Route path="/reports/blocked-workstations" element={<BlockedWorkstationManager />} />
                <Route path="/reports/trespass-requests" element={<TrespassRequestManager />} />
                <Route path="/admin/pk-groups" element={<PKGroupManager />} />
                <Route path="/admin/gpt-configure" element={<GptConfigManager />} />
                <Route path="/inventory/parts-ai-result" element={<PartsAiResult />} />
                <Route path="/reports/usage-summary-monthly" element={<PKUsageSummary type="MONTHLY" />} />
                <Route path="/reports/usage-summary-weekly" element={<PKUsageSummary type="WEEKLY" />} />

                <Route path="/reports/workstation-version" element={<WorkstationVersion />} />
                <Route path="/reports/workstation-ip-analysis" element={<WorkstationIpAnalysis />} />
                <Route path="/reports/workstation-ip-conflict-view" element={<WorkstationIpConflictView />} />
                <Route path="/reports/sync-bandwidth" element={<SyncBandwidth />} />
                <Route path="/reports/storage-size" element={<StorageSize />} />
                <Route path="/reports/data-usage" element={<DataUsage />} />
                <Route path="/ro-folder-demo" element={<ROFolderDemo />} />
                <Route path="/ro-folder-demo/:ro" element={<ROFolderDemo />} />
                <Route path="/ro-folder-demo-img" element={<ROFolderDemoImg />} />
              </Routes>
            </div>
        </div>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
